import React, { useEffect, useState } from "react";
import { Form, Button, Spin } from "antd";
import { Row, Col } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useQueryClient, useMutation } from "react-query";
import toast from "react-hot-toast";
import "../../utils/style.css";
import { addPaymentAccount, updatePayment } from "../../../payements/services/paymentApi";
import { getCountryPayment, getPaymentMethodListByCountryCode } from "../../services/versementApi";
import PhoneNumberInput from "../../../../common/Form/phone/PhoneNumberInput";
import { countriesUemoa } from "../../../../common/Form/phone/countriesUemoa";

const AccountForm = ({ selectedPaymentAccount = null, isEdit = false, onSuccessMobile, onClose }) => {
  const queryClient = useQueryClient();
  const currentUser = JSON.parse(sessionStorage.getItem("authUser"));
  const merchantId = currentUser?.user?.id;
  const token = currentUser?.token;

  const [countryCode, setCountryCode] = useState("SN");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [error, setError] = useState(null);

  const getPaymentMethodListMutation = useMutation(getPaymentMethodListByCountryCode);

  const { mutate, isLoading: isPaymentMethodLoading } = useMutation(getPaymentMethodListByCountryCode, {
    onSuccess: (response) => setPaymentMethods(response),
    onError: (error) => console.error(error),
  });

  useEffect(() => {
    mutate({
      countryCode: countryCode?.toUpperCase(),
      token: token,
    });
  }, [countryCode]);

  const initialValues = selectedPaymentAccount
    ? {
        phone: selectedPaymentAccount.phoneNumber || "",
        prefix: selectedPaymentAccount.prefixCode || "",
        paymentGateway: selectedPaymentAccount.paymentMethod || "",
        countryCode: selectedPaymentAccount.countryCode || "SN",
        accountType: selectedPaymentAccount.accountType,
      }
    : {
        phone: "",
        prefix: "",
        paymentGateway: "",
        countryCode: "SN",
        accountType: "MOBILE_MONEY",
      };

  const validationSchema = Yup.object().shape({
    prefix: Yup.string().required("Veuillez choisir un préfixe"),
    phone: Yup.string().required("Veuillez saisir le numéro"),
    paymentGateway: Yup.string().required("Veuillez choisir un moyen de paiement"),
  });
  
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const data = {
        merchantId: merchantId,
        phoneNumber: values?.phone,
        prefixCode: values?.prefix?.replace("+", ""),
        paymentMethod: values?.paymentGateway,
        ownerFullName: currentUser?.firstname+" "+currentUser?.lastname,
        countryCode: countryCode?.toUpperCase(),
        type: "PAYOUT",
        token : currentUser.token
      };
      try {
        if (selectedPaymentAccount) {
          await updatePayment(selectedPaymentAccount?.id, { ...values });
          toast.success("Compte modifié avec succès");
        } else {
          await addPaymentAccount(data);
          await queryClient.invalidateQueries("accounts");
          toast.success("Compte créé avec succès");
          if (onSuccessMobile) onSuccessMobile();
        }
        setError(null);
        await queryClient.invalidateQueries("accounts");
        formik.resetForm();
        if (onClose) {
          onClose();
        }
      } catch (error) {
        setError(error?.message);        
        console.error("Erreur :", error || error?.message);
        toast.error("Erreur lors de la soumission du compte : " + (error?.message || error));
      }
    },
  });

  useEffect(() => {
    getPaymentMethodListMutation.mutate(countryCode?.toUpperCase(), {
      onSuccess: (response) => {
        setPaymentMethods(response);
      },
      onError: (error) => {
        console.error(error);
      },
    });
  }, [countryCode]);

  const activeCountries = countriesUemoa.filter((country) => country.status === "ACTIVE");
  
  return (
    <div className="account-modal">
      {isEdit && <h5 className="text-center fw-bold mb-4">Modifier le compte</h5>}

      <Form layout="vertical" onFinish={formik.handleSubmit}>
        {error && <div className="alert alert-danger">{error}</div>}
        <Row>
          <Col lg={12} className="mb-3">
            <label className="form-label">Saisir votre numéro de téléphone</label>
            <PhoneNumberInput
              value={{ phone: formik.values.phone }}
              countryCode={formik.values.countryCode}
              countries={activeCountries}
              onChange={({ phone, countryCode, dial }) => {
                formik.setFieldValue("phone", phone);
                formik.setFieldValue("countryCode", countryCode);
                formik.setFieldValue("prefix", dial);

                setCountryCode(countryCode);
              }}
            />
            {formik.touched.phone && formik.errors.phone && (
              <div className="text-danger mt-1">{formik.errors.phone}</div>
            )}
          </Col>

          {isPaymentMethodLoading ? (
            <Spin tip="Chargement..." />
              ) : (
            <div>
              <p>Choisir votre méthode de paiement</p>
              <div className="text-center payment-method-container">
                {paymentMethods.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => formik.setFieldValue("paymentGateway", item.id)}
                    className={`payment-method-card ${formik.values.paymentGateway === item.id ? "selected" : ""}`}
                  >
                    <div className="payment-method-avatar">
                      <img src={item.urlLogo} alt={item.name || "Logo"} />
                    </div>
                    <div className="payment-method-name">
                      {item.name || "Nom inconnu"}
                    </div>
                  </div>
                ))}
              </div>

              {formik.touched.paymentGateway && formik.errors.paymentGateway && (
                <div className="text-danger mt-2">{formik.errors.paymentGateway}</div>
              )}
            </div>
          )}
        </Row>
        <Button
          htmlType="submit"
          type="primary"
          loading={formik.isSubmitting}
          className="btn-confirm-reversement w-100 mt-4"
        >
          {selectedPaymentAccount ? "Modifier" : "Ajouter"}
        </Button>
      </Form>
    </div>
  );
};

export default AccountForm;
